import type { DisciplineGeneralForm } from './interfaces'

import { useDisciplineStore } from './store'
import { useDisciplineGeneralInfoStore } from './general-info/store'
import { useDisciplineUpdateApi } from '../api/update.hook'
import { useDisciplineRefresh } from './discipline-refresh.hook'
import { buildDisciplineUpdatePayload } from './discipline-general-info'

interface DisciplineGeneralFormReturn {
  refForm: ComputedRef<DisciplineGeneralForm | null>
  refIsSaving: ComputedRef<boolean>
  setField: <K extends keyof DisciplineGeneralForm>(key: K, value: DisciplineGeneralForm[K]) => void
  setSubmitUpdate: () => Promise<void>
}

export const useDisciplineGeneralForm = (): DisciplineGeneralFormReturn => {
  const disciplineStore = useDisciplineStore()
  const generalInfoStore = useDisciplineGeneralInfoStore()
  const { refreshDiscipline } = useDisciplineRefresh()

  const refForm = computed<DisciplineGeneralForm | null>(() => generalInfoStore.getForm)
  const refIsSaving = computed<boolean>(() => generalInfoStore.isSaving)

  const setField = <K extends keyof DisciplineGeneralForm>(key: K, value: DisciplineGeneralForm[K]): void => {
    generalInfoStore.setFormField(key, value)
  }

  const setSubmitUpdate = async (): Promise<void> => {
    const details = disciplineStore.getDetails
    const form = generalInfoStore.getForm
    if (!details || !form || generalInfoStore.isSaving) return

    const isUpdated = await useDisciplineUpdateApi({
      disciplineId: details.id,
      payload: buildDisciplineUpdatePayload(details, form),
      onStart: () => generalInfoStore.setSaving(true),
      onFinish: () => generalInfoStore.setSaving(false),
    })

    if (isUpdated) await refreshDiscipline()
  }

  return {
    refForm,
    refIsSaving,
    setField,
    setSubmitUpdate,
  }
}
